import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import api from "@/lib/api";
import { trackLead, trackCompleteRegistration } from "@/lib/meta-pixel";

export default function Register() {
  const navigate = useNavigate();
  const { user, loading, checkAuth } = useAuth();
  const { companyName } = useBranding();

  const [companyNameInput, setCompanyNameInput] = useState("");
  const [adminName, setAdminName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [loading, user, navigate]);

  // Meta Pixel — Lead-event när besökaren når registreringssidan.
  // Signalerar starkt köpintent till Meta:s optimeringsalgoritm.
  useEffect(() => {
    trackLead({ content_name: "register_page_view" });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (companyNameInput.trim().length < 2) {
      toast.error("Företagsnamnet måste vara minst 2 tecken");
      return;
    }
    if (adminName.trim().length < 2) {
      toast.error("Ange kontaktpersonens namn");
      return;
    }
    // Validera svenskt mobilnummer (07x-XXXXXXX)
    const cleanedPhone = phone.replace(/[\s\-()]/g, "");
    if (!/^(?:\+46|0046|0)7[02369]\d{7}$/.test(cleanedPhone)) {
      toast.error("Ange ett giltigt svenskt mobilnummer (07x-XXX XX XX)");
      return;
    }
    if (password.length < 8) {
      toast.error("Lösenordet måste vara minst 8 tecken");
      return;
    }
    if (password !== password2) {
      toast.error("Lösenorden matchar inte");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/orgs/register", {
        company_name: companyNameInput.trim(),
        admin_name: adminName.trim(),
        admin_email: email.trim().toLowerCase(),
        admin_phone: cleanedPhone,
        admin_password: password,
      });
      await checkAuth();
      trackCompleteRegistration({
        content_name: "org_signup",
      });
      toast.success("Företaget är skapat — 30 dagar gratis börjar nu!");
      // Ny flöde 2026-08-25: Ingen Stripe-redirect. Anv får 30 dagars trial
      // utan kort och skickas direkt in i appen. Eskalerande banner nagar
      // dem att aktivera abonnemang innan trial löper ut.
      navigate("/", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Något gick fel";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="register-page"
      className="min-h-screen flex items-center justify-center bg-cover bg-center relative"
      style={{
        backgroundImage:
          'url("https://images.unsplash.com/photo-1591638212063-c2f69701a931?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHx3b29kd29ya2luZyUyMHN0YWlycyUyMG1vZGVybiUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODAxNDQzMDh8MA&ixlib=rb-4.1.0&q=85")',
      }}
    >
      <div className="absolute inset-0 bg-black/55" />
      <div className="relative z-10 bg-surface p-10 md:p-12 rounded-2xl max-w-md w-full mx-4 border border-app">
        <div className="mb-2 text-xs tracking-[0.2em] uppercase text-text-muted">
          {companyName}
        </div>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight text-text-primary leading-[1.05]">
          Skapa ditt <span className="text-brand">företag.</span>
        </h1>
        <p className="mt-4 text-text-muted text-sm">
          Kom igång på 30 sekunder — vi lägger till 5 vanliga moment och ett exempelobjekt så du kan börja stämpla direkt.
        </p>
        <p className="mt-2 text-text-muted text-xs">
          30 dagar gratis. <strong>Inget kort krävs</strong> för att komma igång. Avsluta när som helst.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4">
          <div>
            <label className="block text-xs text-text-muted mb-1.5" htmlFor="company">
              Företagsnamn
            </label>
            <input
              id="company"
              type="text"
              required
              minLength={2}
              maxLength={60}
              data-testid="register-company-input"
              value={companyNameInput}
              onChange={(e) => setCompanyNameInput(e.target.value)}
              className="tk-input w-full"
              placeholder="t.ex. Åkes Konsult AB"
              autoComplete="organization"
            />
          </div>

          <div>
            <label className="block text-xs text-text-muted mb-1.5" htmlFor="admin-name">
              Kontaktperson
            </label>
            <input
              id="admin-name"
              type="text"
              required
              minLength={2}
              maxLength={60}
              data-testid="register-admin-name-input"
              value={adminName}
              onChange={(e) => setAdminName(e.target.value)}
              className="tk-input w-full"
              placeholder="För- och efternamn"
              autoComplete="name"
            />
          </div>

          <div>
            <label className="block text-xs text-text-muted mb-1.5" htmlFor="phone">
              Mobilnummer
            </label>
            <input
              id="phone"
              type="tel"
              required
              data-testid="register-phone-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="tk-input w-full"
              placeholder="07x-XXX XX XX"
              autoComplete="tel"
              inputMode="tel"
            />
          </div>

          <div>
            <label className="block text-xs text-text-muted mb-1.5" htmlFor="email">
              E-post
            </label>
            <input
              id="email"
              type="email"
              required
              data-testid="register-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="tk-input w-full"
              placeholder="namn@foretag.se"
              autoComplete="email"
            />
          </div>

          <div>
            <label className="block text-xs text-text-muted mb-1.5" htmlFor="password">
              Lösenord <span className="text-text-muted">(minst 8 tecken)</span>
            </label>
            <input
              id="password"
              type="password"
              required
              minLength={8}
              data-testid="register-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="tk-input w-full"
              placeholder="••••••••"
              autoComplete="new-password"
            />
          </div>

          <div>
            <label className="block text-xs text-text-muted mb-1.5" htmlFor="password2">
              Upprepa lösenord
            </label>
            <input
              id="password2"
              type="password"
              required
              minLength={8}
              data-testid="register-password2-input"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              className="tk-input w-full"
              placeholder="••••••••"
              autoComplete="new-password"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="register-submit-button"
            className="tk-btn-primary w-full mt-2"
          >
            {submitting ? "Skapar företag…" : "Skapa företag & kom igång"}
          </button>
        </form>

        <p className="mt-6 text-[11px] text-text-muted">
          Har du redan ett konto?{" "}
          <Link to="/login" className="text-brand hover:underline" data-testid="register-login-link">
            Logga in
          </Link>{" "}
          ·{" "}
          <Link to="/pricing" className="text-brand hover:underline" data-testid="register-pricing-link">
            Se priser
          </Link>
        </p>
      </div>
    </div>
  );
}
